import { useEffect, useState, type FunctionComponent } from "react";
import { ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Avatar,
  Button,
  Empty,
  List,
  Modal,
  Segmented,
  Select,
  Skeleton,
  Space,
  Typography,
  message,
} from "antd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { type ChallengeEntity } from "@fitness-app/data-models/entities/Challenge";
import {
  asChallengeFeedMode,
  ChallengeFeedMode,
  type ChallengeFeedPost,
} from "@fitness-app/data-models/entities/ChallengeFeed";

import { supabase, useAppSelector } from "~/store/initializeStore";
import ChallengeFeedPostCard from "./feed/ChallengeFeedPostCard";
import ChallengePostComposerModal from "./feed/ChallengePostComposerModal";
import ChallengePostThreadDrawer from "./feed/ChallengePostThreadDrawer";
import {
  useChallengeFeed,
  useChallengeFeedActions,
  useChallengePostReactors,
  type ChallengeFeedSort,
} from "./feed/useChallengeFeed";
import { CHALLENGE_FEED_MODE_TOOLTIP, challengeFeedModeLabels, challengeFeedModeOptions } from "./constants";

const modeDescription: Record<ChallengeFeedMode, string> = {
  [ChallengeFeedMode.None]: "Tablica jest wyłączona — uczestniczki jej nie widzą.",
  [ChallengeFeedMode.Readonly]: "Uczestniczki widzą posty i mogą reagować emoji, ale nie komentują ani nie publikują.",
  [ChallengeFeedMode.Comments]: "Uczestniczki mogą komentować Twoje posty i reagować emoji.",
  [ChallengeFeedMode.Full]: "Uczestniczki mogą publikować własne posty, komentować i reagować.",
};

const sortOptions = [
  { value: "latest", label: "Najnowsze" },
  { value: "oldest", label: "Najstarsze" },
  { value: "top", label: "Najbardziej aktywne" },
];

/**
 * TRE-1655 — the challenge board as its own page (`/challenges/:id/tablica`). It is a place the
 * trainer works in, not a table wedged into a tab: full-width feed, focused composer, thread drawer.
 */
const ChallengeFeedPage: FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const challengeId = id ?? "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUserName = useAppSelector((store) =>
    [store.user.profile?.firstName, store.user.profile?.lastName].filter(Boolean).join(" "),
  );
  const currentUserAvatar = useAppSelector((store) => store.user.profile?.avatarUrl ?? null);

  const [sort, setSort] = useState<ChallengeFeedSort>("latest");
  const [composerOpen, setComposerOpen] = useState(false);
  const [editedPost, setEditedPost] = useState<ChallengeFeedPost | null>(null);
  const [threadPost, setThreadPost] = useState<ChallengeFeedPost | null>(null);
  const [reactorsPostId, setReactorsPostId] = useState<string | null>(null);

  // TRE-1655 — a notification about a post/comment deep-links here as `?postId=…`. Open that post's
  // thread as soon as it shows up in the loaded feed, then drop the param so a refresh/back doesn't
  // re-open it. If the post is not on the first page(s) yet, we simply leave the feed open.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkedPostId = searchParams.get("postId");

  const challengeQuery = useQuery({
    queryKey: ["challenge-detail", challengeId],
    enabled: Boolean(challengeId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge")
        .select("*")
        .eq("id", challengeId)
        .single<ChallengeEntity>();
      if (error) {
        throw error;
      }
      return data;
    },
  });

  const feedMode = asChallengeFeedMode(challengeQuery.data?.feedMode);
  const feedEnabled = feedMode !== ChallengeFeedMode.None;

  const { posts, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useChallengeFeed(
    challengeId,
    sort,
    Boolean(challengeId) && feedEnabled,
  );
  const actions = useChallengeFeedActions(challengeId);
  const reactorsQuery = useChallengePostReactors(reactorsPostId ?? undefined, Boolean(reactorsPostId));

  useEffect(() => {
    if (!deepLinkedPostId || threadPost) {
      return;
    }
    const target = posts.find((post) => post.id === deepLinkedPostId);
    if (target) {
      setThreadPost(target);
      setSearchParams(
        (prev) => {
          prev.delete("postId");
          return prev;
        },
        { replace: true },
      );
    }
  }, [deepLinkedPostId, posts, threadPost, setSearchParams]);

  const updateFeedMode = useMutation({
    mutationFn: async (mode: ChallengeFeedMode) => {
      const { error } = await supabase
        .from("challenge")
        .update({ feedMode: mode } as never)
        .eq("id", challengeId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void message.success("Tryb tablicy zaktualizowany");
      void queryClient.invalidateQueries({ queryKey: ["challenge-detail", challengeId] });
      void queryClient.invalidateQueries({ queryKey: ["challenge-feed", challengeId] });
    },
    onError: (error: Error) => void message.error(`Nie udało się zapisać: ${error.message}`),
  });

  if (!challengeId) {
    return null;
  }

  const confirmDelete = (post: ChallengeFeedPost) => {
    Modal.confirm({
      title: "Usunąć ten post?",
      content: "Post i wszystkie komentarze pod nim znikną z tablicy uczestniczek.",
      okText: "Usuń",
      okButtonProps: { danger: true },
      cancelText: "Anuluj",
      onOk: () => actions.deletePost.mutateAsync(post.id),
    });
  };

  return (
    <div className="mx-auto w-full max-w-[820px]">
      <Button type="link" className="px-0" icon={<ArrowLeftOutlined />} onClick={() => navigate(`/challenges/${id}`)}>
        Wróć do wyzwania
      </Button>

      <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
        <Space direction="vertical" size={0}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Tablica
          </Typography.Title>
          <Typography.Text type="secondary">
            {challengeQuery.isLoading ? "…" : (challengeQuery.data?.name ?? "")}
          </Typography.Text>
        </Space>

        <Space size={8} wrap>
          <Typography.Text type="secondary" title={CHALLENGE_FEED_MODE_TOOLTIP}>
            Tryb:
          </Typography.Text>
          <Select
            style={{ minWidth: 260 }}
            value={feedMode}
            options={challengeFeedModeOptions}
            loading={updateFeedMode.isLoading}
            onChange={(mode) => updateFeedMode.mutate(mode)}
          />
          {feedEnabled ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditedPost(null);
                setComposerOpen(true);
              }}
            >
              Nowy post
            </Button>
          ) : null}
        </Space>
      </div>

      <Alert
        className="mt-4"
        type={feedEnabled ? "info" : "warning"}
        showIcon
        message={challengeFeedModeLabels[feedMode]}
        description={modeDescription[feedMode]}
      />

      {feedEnabled ? (
        <>
          {/* Circle-style "start a post" affordance: one line, opens the focused composer. */}
          <button
            type="button"
            onClick={() => {
              setEditedPost(null);
              setComposerOpen(true);
            }}
            className="mt-4 flex w-full items-center gap-3 rounded-2xl border border-[#f0f0f0] bg-white p-4 text-left shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-colors hover:border-[#d9d9d9]"
          >
            <Avatar size={36}>{(currentUserName || "T").charAt(0).toUpperCase()}</Avatar>
            <Typography.Text type="secondary">Napisz ogłoszenie do uczestniczek…</Typography.Text>
            <div className="flex-1" />
            <PlusOutlined className="text-[#bfbfbf]" />
          </button>

          <div className="mt-4 flex items-center justify-between">
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {posts.length ? `${posts.length} ${posts.length === 1 ? "post" : "postów"}` : ""}
            </Typography.Text>
            <Segmented value={sort} options={sortOptions} onChange={(value) => setSort(value as ChallengeFeedSort)} />
          </div>

          <div className="mt-4 flex flex-col gap-4">
            {isLoading ? (
              <>
                <Skeleton active avatar paragraph={{ rows: 3 }} />
                <Skeleton active avatar paragraph={{ rows: 3 }} />
              </>
            ) : posts.length ? (
              posts.map((post) => (
                <ChallengeFeedPostCard
                  key={post.id}
                  post={post}
                  onOpenThread={() => setThreadPost(post)}
                  onEdit={() => {
                    setEditedPost(post);
                    setComposerOpen(true);
                  }}
                  onDelete={() => confirmDelete(post)}
                  onTogglePin={() => actions.setPinned.mutate({ id: post.id, pinned: !post.pinned })}
                  onToggleReaction={(emoji) => actions.toggleReaction.mutate({ postId: post.id, emoji })}
                  onShowReactors={() => setReactorsPostId(post.id)}
                />
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-[#e5e5e5] bg-white py-12">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Tablica jest pusta — napisz pierwsze ogłoszenie"
                >
                  <Button
                    type="primary"
                    onClick={() => {
                      setEditedPost(null);
                      setComposerOpen(true);
                    }}
                  >
                    Nowy post
                  </Button>
                </Empty>
              </div>
            )}

            {hasNextPage ? (
              <Button block loading={isFetchingNextPage} onClick={() => void fetchNextPage()}>
                Pokaż starsze posty
              </Button>
            ) : null}
          </div>
        </>
      ) : null}

      <ChallengePostComposerModal
        open={composerOpen}
        challengeId={challengeId}
        challengeName={challengeQuery.data?.name ?? "wyzwaniu"}
        authorName={currentUserName}
        authorAvatar={currentUserAvatar}
        post={editedPost}
        saving={actions.createPost.isLoading || actions.updatePost.isLoading}
        onCancel={() => setComposerOpen(false)}
        onSubmit={({ title, content, media }) => {
          const mutation = editedPost
            ? actions.updatePost.mutateAsync({ id: editedPost.id, title, content, media })
            : actions.createPost.mutateAsync({ title, content, media });
          // A failed save keeps the dialog open with the draft intact (the mutation already toasted).
          void mutation.then(() => setComposerOpen(false)).catch(() => undefined);
        }}
      />

      <ChallengePostThreadDrawer
        post={threadPost}
        challengeId={challengeId}
        onClose={() => setThreadPost(null)}
        isSending={actions.addComment.isLoading}
        onAddComment={(payload) => threadPost && actions.addComment.mutate({ postId: threadPost.id, ...payload })}
        onDeleteComment={(commentId) =>
          threadPost && actions.deleteComment.mutate({ commentId, postId: threadPost.id })
        }
      />

      <Modal
        open={Boolean(reactorsPostId)}
        title="Kto zareagował"
        footer={null}
        onCancel={() => setReactorsPostId(null)}
      >
        <List
          loading={reactorsQuery.isLoading}
          dataSource={reactorsQuery.data ?? []}
          locale={{ emptyText: "Nikt jeszcze nie zareagował" }}
          renderItem={(reactor) => (
            <List.Item extra={<span style={{ fontSize: 18 }}>{reactor.emoji}</span>}>
              <List.Item.Meta
                avatar={
                  <Avatar src={reactor.avatarUrl ?? undefined}>{reactor.displayName.charAt(0).toUpperCase()}</Avatar>
                }
                title={reactor.displayName}
              />
            </List.Item>
          )}
        />
      </Modal>
    </div>
  );
};

export default ChallengeFeedPage;
